import { useState, useEffect, Fragment } from 'react';
import { Archive, Trash2, FlaskConical, PlayCircle } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import RcPill from '../TestExecution/RcPill';
import { promotedFirst } from '../../pages/quality/promoteRun';

function AdminTestManagement() {
  const [cycles, setCycles] = useState([]);
  const [runsByCycle, setRunsByCycle] = useState({});
  const [runDetails, setRunDetails] = useState({});
  const [expandedCycles, setExpandedCycles] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadCycles = () => {
    setLoading(true);
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycles`)
      .then(r => setCycles(r.data))
      .catch(() => setError('Failed to load test cycles.'))
      .finally(() => setLoading(false));
  };

  useEffect(loadCycles, []);

  const fetchRunDetails = (runs) => {
    runs.forEach(run => {
      if (!runDetails[run.testRunId]) {
        api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${run.testRunId}`)
          .then(r => setRunDetails(prev => ({ ...prev, [run.testRunId]: r.data })))
          .catch(() => setRunDetails(prev => ({ ...prev, [run.testRunId]: null })));
      }
    });
  };

  const toggleCycle = (cycle) => {
    setExpandedCycles(prev => {
      const next = new Set(prev);
      if (next.has(cycle.id)) {
        next.delete(cycle.id);
      } else {
        next.add(cycle.id);
        if (!runsByCycle[cycle.id]) {
          api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/runs`, { params: { testCycle: cycle.id } })
            .then(r => {
              setRunsByCycle(prev => ({ ...prev, [cycle.id]: r.data }));
              fetchRunDetails(r.data);
            })
            .catch(() => setRunsByCycle(prev => ({ ...prev, [cycle.id]: [] })));
        } else {
          fetchRunDetails(runsByCycle[cycle.id]);
        }
      }
      return next;
    });
  };

  const handleArchiveCycle = async (cycle) => {
    try {
      const res = await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycle/archive`, { id: cycle.id });
      setCycles(prev => prev.map(c => c.id === cycle.id ? { ...c, endDate: res.data.endDate } : c));
    } catch {
      setError('Archive failed. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'test cycle') {
        await api.delete(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycle/${deleteTarget.id}`);
        setCycles(prev => prev.filter(c => c.id !== deleteTarget.id));
        setRunsByCycle(prev => {
          const next = { ...prev };
          delete next[deleteTarget.id];
          return next;
        });
      } else {
        await api.delete(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/${deleteTarget.id}`);
        setRunsByCycle(prev => {
          const next = { ...prev };
          for (const cycleId of Object.keys(next)) {
            next[cycleId] = next[cycleId].filter(r => r.testRunId !== deleteTarget.id);
          }
          return next;
        });
      }
      setDeleteTarget(null);
    } catch {
      setError('Delete failed. Please try again.');
      setDeleteTarget(null);
    }
  };

  const getPendingCount = (runId) => {
    const detail = runDetails[runId];
    if (!detail || !detail.items) return null;
    return detail.items.filter(i => !i.status || i.status === 'unexecuted' || i.status === 'in-progress').length;
  };

  return (
    <div className="admin-test-mgmt">
      <div className="admin-section-toolbar">
        <h2 className="admin-section-title">Test Management</h2>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : cycles.length === 0 ? (
        <div className="admin-empty">No test cycles found.</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Project</th>
              <th>Version</th>
              <th>Created</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cycles.map(cycle => (
              <Fragment key={cycle.id}>
                <tr className="atm-cycle-row">
                  <td className="admin-td-name">
                    <button className="atm-expand-btn" onClick={() => toggleCycle(cycle)}>
                      {expandedCycles.has(cycle.id) ? '▾' : '▸'}
                    </button>
                    <PlayCircle size={15} className="atm-row-icon" />
                    {cycle.name}
                  </td>
                  <td>{cycle.project}</td>
                  <td><code className="admin-label-chip">{cycle.versionName}</code></td>
                  <td>{cycle.creationDate ? new Date(cycle.creationDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                  <td>
                    {cycle.endDate ? (
                      <span className="atm-archived-badge"><Archive size={12} /> Archived</span>
                    ) : (
                      <span className="atm-active-badge">Active</span>
                    )}
                  </td>
                  <td className="admin-td-actions">
                    {!cycle.endDate && (
                      <button
                        className="admin-btn-archive"
                        onClick={() => handleArchiveCycle(cycle)}
                      >
                        <Archive size={13} /> Archive
                      </button>
                    )}
                    <button
                      className="admin-btn-delete"
                      onClick={() => setDeleteTarget({ type: 'test cycle', id: cycle.id, name: cycle.name })}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </td>
                </tr>
                {expandedCycles.has(cycle.id) && promotedFirst(runsByCycle[cycle.id] || []).map(run => {
                  const pending = getPendingCount(run.testRunId);
                  return (
                    <tr key={run.testRunId} className="atm-run-row">
                      <td className="atm-run-name">
                        {(run.promotedAs ?? []).map(type => <RcPill key={type} type={type} />)}
                        <FlaskConical size={13} className="atm-row-icon" />{run.name}
                      </td>
                      <td></td>
                      <td></td>
                      <td>{run.created ? new Date(run.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td>
                        {pending === null ? (
                          <span className="atm-pending-loading">…</span>
                        ) : pending > 0 ? (
                          <span className="atm-pending-badge">{pending} {pending === 1 ? 'test' : 'tests'} not executed</span>
                        ) : (
                          <span className="atm-done-badge">All tests executed</span>
                        )}
                      </td>
                      <td className="admin-td-actions">
                        <button
                          className="admin-btn-delete"
                          onClick={() => setDeleteTarget({ type: 'test run', id: run.testRunId, name: run.name })}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType={deleteTarget.type}
          entityName={deleteTarget.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default AdminTestManagement;
